import React, { useId, useState } from "react";

import { ScreenIntro, Section } from "../PatternSections";
import HistoryConfirm from "./HistoryConfirm";
import SessionContext from "./SessionContext";
import { sessionIdOf } from "./useSessionContext";
import { HISTORY_SOURCE, HISTORY_STATUS, HISTORY_TEXT, fullWhen } from "./catalogText";
import { labelOf } from "./useHistory";

const T = HISTORY_TEXT;
const D = T.detail;

/**
 * The detail popup's body for one learning event, laid out like the Learning editor's preview: the cause as the title with
 * product · category and when as its meta line (escalated and undone as tags), how the conversation started
 * (SessionContext), then outcome, source, status (with the undo reason and time) and the case id. Where the server
 * allows it, Undo / Redo opens the preview-and-confirm flow (HistoryConfirm) right here; `onDone(action, result)` runs after
 * the server answered.
 */
export default function HistoryDetail({ item, options, onDone }) {
  const id = useId();
  const [confirming, setConfirming] = useState(false);
  const statusKey = item.undone ? HISTORY_STATUS.UNDONE : HISTORY_STATUS.ACTIVE;
  const sourceKey = item.manual ? HISTORY_SOURCE.MANUAL : HISTORY_SOURCE.AUTO;
  const category = [item.product, item.category_label || item.category].filter(Boolean).join(" · ");
  const tags = [
    item.escalated && { label: T.badges.escalated, tone: "warn" },
    item.undone && { label: labelOf(options.statuses, statusKey), tone: "muted" },
  ];
  const undoneNote = item.undone
    ? [item.reason && labelOf(options.undo_reasons, item.reason), item.undone_at && T.row.undoneAt(fullWhen(item.undone_at))].filter(Boolean).join(" ")
    : "";
  return (
    <section className="le-screen" aria-labelledby={`${id}-h`}>
      <ScreenIntro id={`${id}-h`} heading={item.cause_label} meta={D.meta(category, fullWhen(item.when))} tags={tags} />
      <SessionContext sessionId={sessionIdOf(item)} />
      <div className="le-fields">
        <Section label={D.outcome}><p className="le-cause-name">{labelOf(options.kinds, item.kind)}</p></Section>
        <Section label={D.source}>
          <p className="le-text">{labelOf(T.sources, sourceKey)}</p>
        </Section>
        <Section label={D.status}>
          <div className="le-control">
            <p className="le-text">{labelOf(options.statuses, statusKey)}</p>
            {undoneNote && <p className="le-muted">{undoneNote}</p>}
          </div>
        </Section>
        <Section label={D.eid}><p className="le-text"><code>{item.eid}</code></p></Section>
      </div>

      {!item.can_undo && <p className="le-muted">{`${T.row.readOnly}. ${T.row.readOnlyHint}`}</p>}
      {item.can_undo && (confirming
        ? <HistoryConfirm item={item} reasons={options.undo_reasons} onDone={onDone} onCancel={() => setConfirming(false)} />
        : <div className="le-actions"><button type="button" className="le-btn" onClick={() => setConfirming(true)}>{item.undone ? T.row.redo : T.row.undo}</button></div>)}
    </section>
  );
}
